import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Share } from '@travel/icons/ui';
import { useDialogHandler, useTranslation } from '@travel/traveler-core/hooks';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { StarRating, Tag } from '@travel/ui';
import { cx } from '@travel/utils';

import ShareMenu from 'components/ShareMenu';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import { BookmarkButton } from 'pages/ProviderListPage/components/ProviderBox/ProviderHeaderBox';

import { getDetailSummary, getIsFetching } from 'store/providerInformation/selectors';

import useOutsideClick from 'hooks/useOutsideClick';

import JapanQualityAndAward from '../JapanQualityAndAward';
import ShortAddressWithMap from '../ShortAddressWithMap';

import styles from './detailSummary.module.scss';

type Props = {
  /** parent page name for RAT popups */
  pageName: 'hotel:rooms_and_plans_dated_list' | 'hotel:rooms_and_plans_undated_list';
  /** String defining current provider id */
  pageIdentifier: string;
  /** Control show and hide of share icon */
  isShare: boolean;
};

const TitleWithRef = React.forwardRef(
  (props: { title?: string; className?: string }, ref: React.Ref<HTMLHeadingElement>) => {
    return (
      <h1 ref={ref} className={cx(styles.title, props.className)} data-testid="detailSummary-title">
        {props.title}
      </h1>
    );
  },
);

TitleWithRef.displayName = 'TitleWithRef';

function DetailSummary(props: Props) {
  const { pageName, pageIdentifier, isShare } = props;

  const isLoading = useSelector(getIsFetching);
  const detailSummary = useSelector(getDetailSummary(pageIdentifier));

  const { id, categoryNames, catchCopy, starRating, title, isBookmarked } = detailSummary;

  const isMobile = useDeviceType() === MOBILE_SCREEN;

  const { isOpen: hasShareMenu, onToggle: toggleShareMenu } = useDialogHandler(false);
  const [url, setUrl] = useState('');

  const shareMenuRef = useRef(null);
  const shareButtonRef = useRef(null);

  useEffect(() => {
    setUrl(window.location.href);
  }, []);

  const sharePromoTranslation = useTranslation({
    id: 'Traveler_Common.Provider_Info.Share_Title',
    data: {
      provider_deeplink: `${url}`,
    },
  });

  const starRatingLabel = useTranslation({
    id: 'Traveler_Common.Alt.Star_Rating',
    data: { score: starRating },
  });

  const shareButtonLabel = useTranslation({ id: 'Traveler_Common.Alt.Share' });

  const displayedCategoryTag = categoryNames?.find(name => name?.trim().length !== 0);

  useOutsideClick(shareMenuRef, shareButtonRef, toggleShareMenu);

  return (
    <div data-testid="detailSummary-div-wrapper" className={styles.wrapper}>
      <div className={styles.ratingAndButton}>
        <div className={styles.ratingAndTagWrapper}>
          {!!starRating && (
            <StarRating
              className={styles.starRating}
              rating={starRating}
              size={16}
              ariaLabel={starRatingLabel}
            />
          )}

          {displayedCategoryTag && (
            <Tag
              type="outlined"
              className={styles.categoryTag}
              color="blue"
              data-testid="providerInformationPage-detailSummary-categoryTag"
            >
              {displayedCategoryTag}
            </Tag>
          )}
        </div>
        <div className={styles.detailIcons}>
          <BookmarkButton
            className={styles.bookmark}
            providerId={id}
            isBookmarked={Boolean(isBookmarked)}
            iconStyle="gold"
          />
          {isShare && (
            <>
              <button
                ref={shareButtonRef}
                className={styles.shareViaButton}
                onClick={toggleShareMenu}
                data-testid="detailSummary-shareButton"
                aria-label={shareButtonLabel}
              >
                <Share size={40} />
              </button>
              {hasShareMenu && (
                <div ref={shareMenuRef}>
                  <ShareMenu
                    {...(!isMobile && { wrapperClassName: styles.shareMenuWrapperPC })}
                    className={styles.shareMenuStyles}
                    onClose={toggleShareMenu}
                    sharePromoTranslation={sharePromoTranslation}
                    url={url}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {title && <TitleWithRef title={title} />}

      {!isMobile && (
        <WithSkeletonLoading isLoading={isLoading} count={2} direction="column" row={1}>
          <JapanQualityAndAward
            pageName={pageName}
            pageIdentifier={pageIdentifier}
            wrapperClassName={styles.qualityAndAwardWrapper}
          />

          <ShortAddressWithMap pageName={pageName} />

          {catchCopy && (
            <p
              className={styles.catchCopy}
              data-testid="providerInformation-detailSummary-catchCopy"
            >
              {catchCopy}
            </p>
          )}
        </WithSkeletonLoading>
      )}
    </div>
  );
}

export default DetailSummary;
